import { Code, ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { MutationOptions, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'

import {
  getGetIntegrationQueryKey,
  getListAllIntegrationsQueryKey,
} from '@/api/get-integrations.hook'
import {
  createIssue,
  createIssuesBulk,
  disconnectCreatedIssue,
  getCreatedIssues,
  getIssueTypeMetadata,
  getSettings,
  listProjectIssueTypes,
  listProjects,
  setCredentialsAndSettings,
} from '@/gen/jira/v1/jira_service-JiraService_connectquery'
import {
  CreateIssueRequest,
  Credentials,
  GetIssueTypeMetadataResponse,
  JiraService,
  SetCredentialsAndSettingsResponse,
} from '@/gen/jira/v1/jira_service_pb.ts'

import { defaultTransport } from '@/lib/connect/connect-transport.ts'

// --- Query Key Factories ---

export const getGetCreatedIssuesQueryKey = () =>
  createConnectQueryKey({
    schema: JiraService.method.getCreatedIssues,
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetSettingsQueryKey = () =>
  createConnectQueryKey({
    schema: JiraService.method.getSettings,
    transport: defaultTransport,
    cardinality: 'finite',
  })

// --- Hooks ---

export const useSetCredentialsAndSettings = (
  mutationOptions?: MutationOptions<SetCredentialsAndSettingsResponse, Error, unknown>,
) => {
  const queryClient = useQueryClient()
  const { onSuccess: originalOnSuccess, ...restOptions } = mutationOptions || {}

  return useMutation(setCredentialsAndSettings, {
    ...restOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      if (originalOnSuccess) {
        originalOnSuccess(data, variables, onMutateResult, context)
      }
      queryClient.invalidateQueries({ queryKey: getGetIntegrationQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListAllIntegrationsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() })
    },
  })
}

// useListProjectIssueTypesAsync hook to list issue types for a project
// when credentials are omitted it uses the credentials stored in db
// IMPORTANT issue types marked with subtype:true must be created with a parent
export const useListProjectIssueTypes = ({
  projectId,
  credentials,
  enabled = true,
}: {
  projectId: string
  credentials?: Credentials
  enabled?: boolean
}) => {
  return useQuery(
    listProjectIssueTypes,
    {
      projectId,
      credentials,
    },
    {
      enabled,
    },
  )
}

export const useListProjects = ({
  credentials,
  enabled = true,
}: {
  credentials?: Credentials
  enabled?: boolean
}) => {
  return useQuery(listProjects, { credentials }, { enabled })
}

// List Jira projects with custom credentials
export const useListJiraProjectsAsync = () => {
  return useMutation(listProjects)
}

// useListProjectIssueTypesAsync hook to list issue types for a project
// when credentials are omitted it uses the credentials stored in db
// IMPORTANT issue types marked with subtype:true must be created with a parent
export const useListProjectIssueTypesAsync = () => {
  return useMutation(listProjectIssueTypes)
}

export const useGetIssueTypeMetadata = () => {
  return useMutation(getIssueTypeMetadata)
}

export const useGetIssueTypeRequiredFields = ({
  projectIdOrKey,
  issueTypeId,
  credentials,
  enabled = true,
}: {
  projectIdOrKey: string
  issueTypeId: string
  credentials?: Credentials
  enabled?: boolean
}) => {
  return useQuery(
    getIssueTypeMetadata,
    {
      credentials,
      projectIdOrKey,
      issueTypeId,
    },
    {
      enabled,
      select: (data) => requiredFieldsFromIssueTypeMetadata(data),
    },
  )
}

export type JiraIssueField = {
  fieldId: string
  name: string
  required: boolean
  hasDefaultValue: boolean
  defaultValue?: {
    id: string
  }
  allowedValues?: {
    value: string
    name: string
    id: string
  }[]
  schema: {
    type: string // for example: 'string', 'number', 'option' etc
  }
}

const jiraFieldsFromIssueTypeMetadata = (
  issueTypeMetadata: GetIssueTypeMetadataResponse,
): JiraIssueField[] => {
  return issueTypeMetadata.fields.map((field) => {
    return {
      fieldId: field.fieldId as string,
      name: field.name as string,
      required: field.required as boolean,
      hasDefaultValue: field.hasDefaultValue as boolean,
      defaultValue:
        field.defaultValue && typeof field.defaultValue === 'object' && 'id' in field.defaultValue
          ? {
              id: field.defaultValue.id as string,
            }
          : undefined,
      allowedValues: (field.allowedValues as JiraIssueField['allowedValues'] | undefined)?.map(
        (allowedValue) => ({
          value: allowedValue.value as string,
          name: allowedValue.name as string,
          id: allowedValue.id as string,
        }),
      ),
      schema: {
        type: (field.schema as JiraIssueField['schema']).type as string,
      },
    }
  })
}

export const requiredFieldsFromIssueTypeMetadata = (
  issueTypeMetadata: GetIssueTypeMetadataResponse,
) => {
  // Fields that are always required
  const blacklistFields = [
    'summary',
    'description',
    'project',
    'issuetype',
    'reporter',
    'parent',
    'assignee',
  ]

  try {
    const fields = jiraFieldsFromIssueTypeMetadata(issueTypeMetadata)
    return fields.filter((field) => !blacklistFields.includes(field.fieldId) && field.required)
  } catch (error) {
    console.error('Error parsing issue type metadata', error)
    return []
  }
}

export const isJiraIssueTooLargeError = (error: unknown) => {
  const connectErr = ConnectError.from(error)
  return connectErr.code === Code.InvalidArgument && connectErr.message.includes('too large')
}

export const isJiraNoPermissionError = (error: unknown) => {
  const connectErr = ConnectError.from(error)
  return (
    connectErr.code === Code.PermissionDenied &&
    connectErr.message.includes('you do not have permission to create issues in this project')
  )
}

// Create Jira issue, when credentials are omitted, it uses the credentials stored in db
// IMPORTANT: handle the case when the issue is too large, use isJiraIssueTooLargeError.
// Also handle the case when the user does not have permission to create issues, use isJiraNoPermissionError
export const useCreateJiraIssue = () => {
  const queryClient = useQueryClient()
  return useMutation(createIssue, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCreatedIssuesQueryKey() })
    },
  })
}

// Create multiple Jira issues in bulk up to 50 per request
// IMPORTANT: you should probably use useCreateJiraIssuesBulkWithChunking instead of this
export const useCreateJiraIssuesBulk = () => {
  const queryClient = useQueryClient()
  return useMutation(createIssuesBulk, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCreatedIssuesQueryKey() })
    },
  })
}

// Create multiple Jira issues in bulk with automatic chunking (handles >50 issues)
// Automatically chunks issues into batches of 50 (Jira's limit) and processes them sequentially
// Returns aggregated results from all chunks
// NOTE: handle the case when the user does not have permission to create issues, use isJiraNoPermissionError
export const useCreateJiraIssuesBulkWithChunking = () => {
  const { mutateAsync: createJiraIssuesBulk } = useCreateJiraIssuesBulk()
  const CHUNK_SIZE = 50

  return {
    mutateAsync: async (request: { issues: Array<CreateIssueRequest> }) => {
      if (!request.issues || request.issues.length === 0) {
        return { issues: [], errors: [] }
      }

      // Process in chunks
      const allCreatedIssues: Array<{ id: string; key: string; link: string }> = []
      const allErrors: Array<{ issueIndex: number; message: string; statusCode: number }> = []

      const chunks = _.chunk(request.issues, CHUNK_SIZE)

      // Process chunks sequentially
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex]
        if (!chunk) {
          continue
        }

        try {
          let bulkResponse
          let attempt = 0
          const MAX_RETRIES = 5

          do {
            attempt++
            bulkResponse = await createJiraIssuesBulk({
              issues: chunk,
            })

            // Retry ONLY if all issues failed with errors and we haven't reached the max retries
            // This is here to prevent general errors from causing the request to fail completely
            // For example, if the Jira API has a delay in completing the first task creation, retrying will help
            if (
              bulkResponse.errors &&
              bulkResponse.errors.length > 0 &&
              bulkResponse.issues.length === 0 &&
              attempt < MAX_RETRIES
            ) {
              console.error(
                `Failed to bulk create subtasks (chunk ${chunkIndex + 1} of ${chunks.length}, attempt ${attempt} out of ${MAX_RETRIES}):`,
                bulkResponse.errors,
              )
              // Exponential backoff: 1s, 2s, 3s, 4s
              await new Promise((resolve) => setTimeout(resolve, 1000 * attempt))
              continue
            }
            break
          } while (attempt < MAX_RETRIES)

          allCreatedIssues.push(...bulkResponse.issues)

          // Adjust error indices to be relative to the full request
          for (const issueErr of bulkResponse.errors) {
            allErrors.push({
              ...issueErr,
              issueIndex: chunkIndex * CHUNK_SIZE + issueErr.issueIndex,
            })
          }
        } catch (err) {
          // If a chunk fails completely, log error and continue
          console.error(`Failed to create chunk ${chunkIndex + 1} of ${chunks.length}:`, err)
          // Mark all issues in this chunk as failed
          for (let i = 0; i < chunk.length; i++) {
            allErrors.push({
              issueIndex: chunkIndex * CHUNK_SIZE + i,
              message: err instanceof Error ? err.message : 'Unknown error',
              statusCode: 500,
            })
          }
        }
      }

      return {
        issues: allCreatedIssues,
        errors: allErrors,
      }
    },
  }
}

export const useGetJiraSettings = () => {
  return useQuery(getSettings)
}

// Gets issues created through Lema INCLUDING subtasks
export const useGetJiraCreatedIssues = ({
  orgCompanyId,
  enabled = true,
}: {
  orgCompanyId: string
  enabled?: boolean
}) => {
  return useQuery(
    getCreatedIssues,
    {
      orgCompanyId,
    },
    {
      enabled: enabled ?? true,
    },
  )
}

export const useDisconnectCreatedIssue = () => {
  const queryClient = useQueryClient()
  return useMutation(disconnectCreatedIssue, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetCreatedIssuesQueryKey() })
    },
  })
}
